import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, ArrowRight, X } from 'lucide-react';
import { useCart } from '../context/CartContext';

/**
 * Swiggy-style sticky "View Cart" bar.
 * Appears once the cart has any items. Hidden on /cart and /checkout.
 * The user can dismiss it with the X — it re-appears the next time the
 * cart count changes (i.e. a new item is added).
 */
export default function ViewCartBar() {
  const { count, subtotal } = useCart();
  const { pathname } = useLocation();
  const [dismissedAt, setDismissedAt] = useState(-1);

  // Any time the count changes, re-show the bar (user added / removed items)
  useEffect(() => {
    setDismissedAt(-1);
  }, [count]);

  if (count <= 0) return null;
  if (dismissedAt === count) return null;
  // Hide on routes that already have their own sticky Add-to-Cart / cart summary bar.
  // The PDP has its own sticky bar (with the product image, qty controls and Add button),
  // /cart & /checkout obviously don't need a floating cart reminder, and account pages
  // (especially the address form with its on-screen keyboard) must stay clear.
  if (
    pathname.startsWith('/cart') ||
    pathname.startsWith('/checkout') ||
    pathname.startsWith('/product/') ||
    pathname.startsWith('/account') ||
    pathname.startsWith('/order/')
  ) return null;

  const label = count === 1 ? '1 item added' : `${count} items added`;

  return (
    <div
      role="region"
      aria-label="View cart"
      data-testid="view-cart-bar"
      className="fixed inset-x-0 bottom-4 sm:bottom-6 z-50 pointer-events-none px-3 sm:px-4"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="max-w-md mx-auto">
        <div className="pointer-events-auto flex items-stretch rounded-full overflow-hidden bg-[#2f6b47] shadow-[0_10px_30px_-8px_rgba(13,40,24,0.55)] ring-1 ring-black/10 backdrop-blur">
          <Link
            to="/cart"
            data-testid="view-cart-bar-link"
            className="flex-1 flex items-center justify-between gap-3 h-11 pl-2.5 pr-3 hover:bg-white/5 active:bg-white/10 transition-colors group"
          >
            <div className="flex items-center gap-2.5 text-white min-w-0">
              <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 flex-shrink-0">
                <ShoppingBag size={14} strokeWidth={2.25} aria-hidden="true" />
                <span
                  className="absolute -top-1 -right-1 bg-white text-[#2d6644] text-[9px] font-bold min-w-[15px] h-[15px] px-1 rounded-full flex items-center justify-center"
                  aria-hidden="true"
                >
                  {count}
                </span>
              </span>
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[13px] font-semibold truncate">{label}</span>
                <span className="text-[10px] text-white/70 truncate">
                  ₹{subtotal.toFixed(0)} • Tap to review
                </span>
              </div>
            </div>

            <span className="flex items-center gap-1 text-white font-semibold text-[12px] tracking-[0.08em] flex-shrink-0 ml-1 uppercase">
              View Cart
              <ArrowRight
                size={13}
                className="transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </span>
          </Link>

          {/* Dismiss */}
          <button
            type="button"
            onClick={() => setDismissedAt(count)}
            aria-label="Hide view cart bar"
            data-testid="view-cart-bar-close"
            className="pointer-events-auto flex items-center justify-center w-9 h-11 text-white/80 hover:text-white hover:bg-white/10 active:bg-white/15 border-l border-white/15 transition-colors"
          >
            <X size={14} strokeWidth={2.25} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
